/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 * @edit   Tomáš Burian    (burian@cadwork.cz)
 *
 */

import {RayIntersector, RenderDocument} from "js_renderer";
import Picker from "./picker";
import Point from "../../elements/point";
import {vec3} from "gl-matrix";
import PrimitivePoint from "../../primitive_point";

export default
class PrimitivePointPicker extends Picker {
    private _highlightStaticPoint: Point;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._highlightStaticPoint = null;
    }

    public startPicking() {
        if (this._enabled) {
            return false;
        }

        this._highlightStaticPoint = this._createPrimitivePointForHighlightingCurrentPoint();

        super.startPicking();
    }

    public stopPicking() {
        if (!this._enabled) {
            return false;
        }

        this._highlightStaticPoint.remove();
        this._highlightStaticPoint = null;

        super.stopPicking();
    }

    public getClosestPrimitivePointInfo() {
        let view = this._sceneAPI.viewModeModule.getActiveView();

        if (!view) {
            return null;
        }

        let physMousePosition = view.getLastMousePosition();
        if (!physMousePosition) {
            return null;
        }

        let pickingInfo = this._renderDocument.renderForValues(view, {
            type: "cdwk_primitive_id",
            readPosition: [physMousePosition[0], physMousePosition[1]]
        });

        // the picking info contains reference to shape and a primitiveIndex i.e. index to face (triangles, points, line segments)
        if (pickingInfo.shapeRef) {
            // in order to determine the closest point, we need the primitive points transformed by the current manipulator transformation, if is is active for
            // current model
            let manipulatorTrafo = null;
            let activeObject = this._sceneAPI.activityModule.getActiveObject();
            if (activeObject && activeObject.getUID() === pickingInfo.shapeRef.model.getSceneAPIModelUID()) {
                manipulatorTrafo = this._sceneAPI.activityModule.getCurrentManipulatorTransformation();
            }
            else {
                // model transformation
                // the manipulator trafo principle is deprecated and will be removed
                manipulatorTrafo = pickingInfo.shapeRef.getModel().getSceneAPIModel().getModelTransformationRef();
            }

            let closestPoint = RayIntersector.getClosestPoint(
                pickingInfo.shapeRef.getGeometry(),
                manipulatorTrafo,
                pickingInfo.shapeRef.getPrimitiveSize(),
                pickingInfo.primitiveIndex,
                view,
                physMousePosition[0],
                physMousePosition[1]
            );

            if (closestPoint !== undefined) {

                return {
                    position: vec3.clone(closestPoint.position),
                    geometryObjectUID: pickingInfo.geometryObjectUID,
                    primitiveIndex: pickingInfo.primitiveIndex,
                    vertexIndex: closestPoint.index
                };
            }
        }

        return null;
    }

    public onMove(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        if (!this._enabled || aDraggedDuringClick || this.isPaused()) {
            return false;
        }

        let primitivePoint = null;
        if (this._callbacks.onMove !== undefined) {
            let pointInfo = this.getClosestPrimitivePointInfo();
            if (pointInfo) {
                primitivePoint = new PrimitivePoint(this._sceneAPI);
                primitivePoint.setInfo(pointInfo);
            }

            this._callbacks.onMove(primitivePoint);
        }

        // for current primitive point highlighting handled by this picker
        if (this._highlighOnMove) {
            if (!primitivePoint) {
                primitivePoint = this.getClosestPrimitivePointInfo();

                if (this._highlightStaticPoint && !primitivePoint) {
                    this._highlightStaticPoint.setVisibility(false);
                    return;
                }
            }

            this._highlightStaticPoint.setPosition(primitivePoint.position);
            this._highlightStaticPoint.setVisibility(true);
            this._highlightStaticPoint.draw(true);
            this._sceneAPI.viewModeModule.scheduleRedraw();
        }
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        if (!this._enabled || aDraggedDuringClick || this.isPaused()) {
            return false;
        }

        if (this._callbacks.onClick !== undefined) {
            let pointInfo = this.getClosestPrimitivePointInfo();
            if (!pointInfo) {
                return;
            }

            let primitivePoint = new PrimitivePoint(this._sceneAPI);
            primitivePoint.setInfo(pointInfo);

            this._callbacks.onClick(primitivePoint);
        }
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number) {
        this.onMove(x, y, 0, false);
    }

    public onTouchEnd(x: number, y: number, remainingTouch: number, aDraggedDuringTouch: number) {
        this.onSingleTap(x, y);
    }

    public onSingleTap(x: number, y: number) {
        if (!this._enabled || this.isPaused()) {
            return false;
        }

        if (this._callbacks.onClick !== undefined) {
            let pointInfo = this.getClosestPrimitivePointInfo();
            if (!pointInfo) {
                return;
            }

            let primitivePoint = new PrimitivePoint(this._sceneAPI);
            primitivePoint.setInfo(pointInfo);

            this._callbacks.onSingleTap(primitivePoint);
        }
    }

    private _createPrimitivePointForHighlightingCurrentPoint(): Point {
        let element = this._sceneDocument.createAPIElement(Point.TypeName) as Point;
        this._sceneDocument.attachElementToInternalModel(element);
        // element.setPosition(pointPlanetPosition);

        // these are default properties so it is not necessary to set them here
        element.extendProperties({
            // minPointSize: 12.0,
            // maxPointSize: 12.0,
            // glyphPosition: [0.0 / Point.texture_size[0], 0.0 / Point.texture_size[1]],
            alwaysOnTop: true,
            pickable: false
        });

        return element;
    }
}
